import { BOTONES } from '../../componentes/botones';
import { TooltipDirective } from '../tooltip/tooltip.directive';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  HostListener,
} from '@angular/core';

import { CameraStream } from '../multi-camera-stream/multi-camera-stream.component';
import { MosaicVideoComponent } from '../mosaic-video/mosaic-video.component';

export type MosaicGridSize = 1 | 4 | 9 | 16;

const GRID_COLS: Record<MosaicGridSize, number> = { 1: 1, 4: 2, 9: 3, 16: 4 };

@Component({
  selector: 'app-multi-camera-mosaic',
  standalone: true,
  imports: [MosaicVideoComponent, BOTONES, TooltipDirective],
  templateUrl: './multi-camera-mosaic.component.html',
  styleUrls: ['./multi-camera-mosaic.component.scss'],
})
export class MultiCameraMosaicComponent implements OnInit {
  @Input() streams: CameraStream[] = [];
  /** Cámara+canal con el que abrir en el modo de 1 cámara (p. ej. la zona
   * del evento). Sin esto arranca en la grilla de 4. */
  @Input() inicial?: { idCamara: string; canal: string };
  /** Instante del evento (ISO). Si viene, la barra de grabación muestra
   * los rebobinados desde la hora del evento en lugar del selector de fecha. */
  @Input() fechaEvento?: string;
  @Output() back = new EventEmitter<void>();

  public gridSize: MosaicGridSize = 4;
  public currentPage: number = 0;
  public readonly gridSizes: MosaicGridSize[] = [1, 4, 9, 16];
  public slotRatio: '16/9' | '4/3' = '16/9';
  public showCameraList: boolean = false;
  public selectedDate: string = '';

  ngOnInit(): void {
    if (!this.inicial) return;
    const stream = this.streams.find(
      (s) =>
        s.camara._id === this.inicial.idCamara &&
        s.canal === this.inicial.canal,
    );
    if (stream) this.viewSingle(stream);
  }

  get cols(): number {
    return GRID_COLS[this.gridSize];
  }

  get rows(): number {
    return Math.ceil(this.gridSize / this.cols);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.streams.length / this.gridSize));
  }

  get isFirstPage(): boolean {
    return this.currentPage === 0;
  }

  get isLastPage(): boolean {
    return this.currentPage >= this.totalPages - 1;
  }

  get currentSlots(): (CameraStream | null)[] {
    const start = this.currentPage * this.gridSize;
    const slots: (CameraStream | null)[] = [
      ...this.streams.slice(start, start + this.gridSize),
    ];
    while (slots.length < this.gridSize) slots.push(null);
    return slots;
  }

  // Los cambios de grilla/página se aplican directo: la grilla trackea los
  // slots por cámara+canal, así que Angular conserva los players que siguen
  // en pantalla y solo crea/destruye la diferencia. Antes se ocultaba toda la
  // grilla 80 ms "para forzar ngOnDestroy": eso dejaba cada canal sin
  // viewers un instante, go2rtc mataba y rehacía TODAS las sesiones RTSP y
  // el DVR (que todavía contaba las recién matadas) rechazaba las nuevas.
  // Los players que salen cierran su RTCPeerConnection en ngOnDestroy igual.

  /** Cámara+canal seleccionado con un clic en el mosaico (null = ninguno). */
  public selectedKey: string | null = null;

  public streamKey(stream: CameraStream): string {
    return `${stream.camara._id}_${stream.canal}`;
  }

  /** Stream seleccionado, solo si está a la vista en la página actual. */
  get selectedStream(): CameraStream | null {
    if (!this.selectedKey) return null;
    return (
      this.currentSlots.find(
        (s) => !!s && this.streamKey(s) === this.selectedKey,
      ) ?? null
    );
  }

  /** "Canal N · nombre del canal · cliente · tipo", con lo que haya cargado. */
  get selectedDetalle(): string {
    const s = this.selectedStream;
    if (!s) return '';
    const canal = s.camara.canales?.find((c) => c.numero === s.canal);
    const etiqueta = `Canal ${s.canal}`;
    // Muchos canales tienen cargado como nombre "Canal N": no repetirlo.
    const nombre =
      canal?.nombre?.trim().toLowerCase() === etiqueta.toLowerCase()
        ? null
        : canal?.nombre;
    return [etiqueta, nombre, s.camara.cliente?.nombre, s.camara.tipo]
      .filter(Boolean)
      .join(' · ');
  }

  /** Clic en un slot: lo selecciona, o lo deselecciona si ya lo estaba. */
  public toggleSelect(stream: CameraStream): void {
    const key = this.streamKey(stream);
    this.selectedKey = this.selectedKey === key ? null : key;
  }

  public clearSelection(): void {
    this.selectedKey = null;
  }

  /** Pasa a la vista de 1 cámara mostrando la seleccionada (o la indicada). */
  public viewSingle(stream: CameraStream | null = this.selectedStream): void {
    if (!stream) return;
    const key = this.streamKey(stream);
    const index = this.streams.findIndex((s) => this.streamKey(s) === key);
    if (index < 0) return;
    this.selectedKey = key;
    this.showCameraList = false;
    this.gridSize = 1;
    this.currentPage = index;
  }

  /** Al cambiar de grilla se va a la página que contiene la cámara
   * seleccionada (o la que se estaba viendo en modo 1), así volver del
   * modo 1 cámara deja a la vista la misma cámara. */
  public setGridSize(size: MosaicGridSize): void {
    this.showCameraList = false;
    const key =
      this.gridSize === 1 && this.currentStream
        ? this.streamKey(this.currentStream)
        : this.selectedKey;
    const index = key
      ? this.streams.findIndex((s) => this.streamKey(s) === key)
      : -1;
    this.gridSize = size;
    this.currentPage = index >= 0 ? Math.floor(index / size) : 0;
    if (index >= 0 && size > 1) this.selectedKey = key;
  }

  public goToCamera(index: number): void {
    this.showCameraList = false;
    this.currentPage = index;
  }

  public prevPage(): void {
    if (this.isFirstPage) return;
    this.currentPage--;
  }

  public nextPage(): void {
    if (this.isLastPage) return;
    this.currentPage++;
  }

  /** Vista de 1 cámara: ← → pasan a la cámara anterior/siguiente, Home/End
   * a la primera/última y 1-9 van directo a esa cámara. */
  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.gridSize !== 1 || this.streams.length < 2) return;
    if (event.ctrlKey || event.altKey || event.metaKey) return;
    // Mantener apretada la flecha recorrería todas las cámaras abriendo y
    // cerrando una sesión RTSP por cada una.
    if (event.repeat) return;
    // Sin robarle teclas al selector de fecha de la barra de grabación.
    const target = event.target as HTMLElement | null;
    if (
      target?.isContentEditable ||
      ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName ?? '')
    ) {
      return;
    }

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.showCameraList = false;
        this.prevPage();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.showCameraList = false;
        this.nextPage();
        break;
      case 'Home':
        event.preventDefault();
        this.goToCamera(0);
        break;
      case 'End':
        event.preventDefault();
        this.goToCamera(this.streams.length - 1);
        break;
      default:
        if (/^[1-9]$/.test(event.key)) {
          const index = Number(event.key) - 1;
          if (index < this.streams.length) {
            event.preventDefault();
            this.goToCamera(index);
          }
        }
    }
  }

  /** Ahora, en hora local y en el formato del input datetime-local
   * ("YYYY-MM-DDTHH:mm"). Con toISOString() salía en UTC y dejaba elegir
   * hasta 3 horas en el futuro. */
  get maxDate(): string {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  }

  get currentStream(): CameraStream | null {
    return this.currentSlots[0] ?? null;
  }

  get isRecordingMode(): boolean {
    return !!this.currentStream?.recordDate;
  }

  /** Cambia el stream actual a modo grabación. El camera-player detecta
   * el cambio de [fecha] via ngOnChanges y re-fetchea internamente. */
  public watchRecording(): void {
    const stream = this.currentStream;
    if (!stream || !this.selectedDate) return;
    // El input datetime-local da la hora local sin zona ("2026-10-01T00:53").
    // La API espera un instante en UTC (como el app-date-picker de la grilla):
    // mandado sin zona, el servidor (en UTC) lo tomaba como UTC y la
    // grabación salía 3 horas antes de la hora elegida.
    const fecha = new Date(this.selectedDate);
    if (isNaN(fecha.getTime())) return;
    stream.recordDate = fecha.toISOString();
  }

  public readonly rebobinados: { min: number; label: string }[] = [
    { min: 0.5, label: '30s' },
    { min: 1, label: '1m' },
    { min: 3, label: '3m' },
    { min: 5, label: '5m' },
  ];

  /** Grabación desde `min` minutos antes del evento (0 = hora del evento).
   * fechaEvento ya es un instante en UTC, que es lo que espera la API. */
  public volverAtras(min: number): void {
    const stream = this.currentStream;
    if (!stream || !this.fechaEvento) return;
    const tiempo = new Date(this.fechaEvento);
    if (isNaN(tiempo.getTime())) return;
    if (min > 0) tiempo.setSeconds(tiempo.getSeconds() - min * 60);
    // Repetir el mismo botón no cambia [fecha] y el player no re-fetchea:
    // restar un segundo fuerza el cambio y vuelve a arrancar la grabación.
    if (stream.recordDate === tiempo.toISOString()) {
      tiempo.setSeconds(tiempo.getSeconds() - 1);
    }
    stream.recordDate = tiempo.toISOString();
  }

  /** Vuelve a vivo limpiando recordDate. */
  public goLive(): void {
    const stream = this.currentStream;
    if (!stream) return;
    stream.recordDate = undefined;
  }
}
